import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Residents() {
  const { user } = useAuth();
  const [residents, setResidents] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", unit_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get("/users", { params: { role: "resident" } }),
        api.get("/units"),
      ]);
      setResidents(r.data); setUnits(u.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = { name: form.name, phone: form.phone };
        if (form.password) patch.password = form.password;
        if (form.unit_id !== undefined) patch.unit_id = form.unit_id || null;
        await api.patch(`/users/${editing.id}`, patch);
        // handle unit reassignment
        if (form.unit_id && form.unit_id !== editing.unit_id) {
          await api.post(`/units/${form.unit_id}/assign`, { resident_id: editing.id });
        }
        toast.success("Resident updated");
      } else {
        const { data } = await api.post("/residents", {
          name: form.name, email: form.email, password: form.password,
          phone: form.phone, estate_id: user.estate_id, unit_id: form.unit_id || null,
        });
        if (form.unit_id) {
          await api.post(`/units/${form.unit_id}/assign`, { resident_id: data.id });
        }
        toast.success("Resident created");
      }
      setOpen(false); setEditing(null);
      setForm({ name: "", email: "", password: "", phone: "", unit_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this resident?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!user?.estate_id) {
    return <AppShell><PageContainer>
      <PageHeader eyebrow="Estate Manager" title="Residents" />
      <EmptyState title="No estate assigned" description="You need an estate before adding residents." />
    </PageContainer></AppShell>;
  }

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow="Estate Manager"
          title="Residents"
          description="Create resident accounts and link them to units."
          actions={
            <Button
              onClick={() => { setEditing(null); setForm({ name: "", email: "", password: "", phone: "", unit_id: "" }); setOpen(true); }}
              data-testid="create-resident-button"
              className="rounded-none bg-black hover:bg-zinc-800 h-10 px-4 uppercase tracking-wider text-xs font-semibold"
            >
              <Plus className="h-4 w-4" /> New Resident
            </Button>
          }
        />

        {loading ? <div className="label-eyebrow">Loading…</div> :
          residents.length === 0 ? (
            <EmptyState title="No residents yet" description="Add the first resident to your estate." />
          ) : (
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="border-b border-zinc-200 bg-zinc-50">
                  <tr className="text-left">
                    <th className="p-4 label-eyebrow font-semibold">Name</th>
                    <th className="p-4 label-eyebrow font-semibold">Email</th>
                    <th className="p-4 label-eyebrow font-semibold">Phone</th>
                    <th className="p-4 label-eyebrow font-semibold">Unit</th>
                    <th className="p-4 label-eyebrow font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {residents.map((r) => {
                    const unit = units.find((u) => u.id === r.unit_id);
                    return (
                      <tr key={r.id} className="border-b border-zinc-100" data-testid={`resident-row-${r.id}`}>
                        <td className="p-4 font-semibold">{r.name}</td>
                        <td className="p-4 text-zinc-600">{r.email}</td>
                        <td className="p-4 text-zinc-600">{r.phone || "—"}</td>
                        <td className="p-4">
                          {unit ? (
                            <div className="text-xs">
                              {unit.block && <span>{unit.block} · </span>}
                              <span className="font-semibold">#{unit.house_number}</span>
                              {unit.apartment_number && <span> / {unit.apartment_number}</span>}
                            </div>
                          ) : <span className="text-xs text-zinc-400 uppercase tracking-wider">Unassigned</span>}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setEditing(r); setForm({ name: r.name, email: r.email, password: "", phone: r.phone || "", unit_id: r.unit_id || "" }); setOpen(true); }}
                              data-testid={`edit-resident-${r.id}`}
                              className="p-2 border border-zinc-200 hover:bg-black hover:text-white hover:border-black transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                            <button onClick={() => onDelete(r.id)}
                              data-testid={`delete-resident-${r.id}`}
                              className="p-2 border border-zinc-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent className="rounded-none border-zinc-300" data-testid="resident-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {editing ? "Edit Resident" : "New Resident"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="resident-name-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
                </div>
                <div>
                  <Label className="label-eyebrow">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="resident-phone-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Email</Label>
                <Input required type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="resident-email-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">{editing ? "New Password (optional)" : "Password"}</Label>
                <Input required={!editing} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="resident-password-input" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black mt-2" />
              </div>
              <div>
                <Label className="label-eyebrow">Unit</Label>
                <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-none border-zinc-300 mt-2" data-testid="resident-unit-select"><SelectValue placeholder="Select a unit" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {units.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {[u.block, `#${u.house_number}`, u.apartment_number].filter(Boolean).join(" · ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-resident-button" className="rounded-none bg-black hover:bg-zinc-800 uppercase tracking-wider text-xs font-semibold">
                  {editing ? "Save Changes" : "Create Resident"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </AppShell>
  );
}
